import { useMemo } from 'react';

import { useGetAccountRelated } from '~/hooks/apis/contracting/useGetAccountRelated';
import { useGetAccountRelatedList } from '~/hooks/apis/contracting/useGetAccountRelatedList';
import { useGetContractingAccountId } from '~/hooks/apis/contracting/useGetContractingAccountId';

import type { AccountRelatedListKey, AccountRelatedRow } from '~/services/ContractingService';

import { accountLoadErrorReason } from './accountLoadErrorReason';
import { RELATED_PREVIEW_LIMIT } from './accountRelatedModel';

interface QueryState {
  error: Error | null;
  isLoading: boolean;
}

function toLoadState(idQuery: QueryState, query: QueryState) {
  const error = idQuery.error ?? query.error;
  return {
    error: error && new Error(accountLoadErrorReason(error)),
    isLoading: idQuery.isLoading || query.isLoading,
  };
}

// The Related page's cards share one summary query, so the page costs one request.
export function useAccountRelatedPreview<K extends AccountRelatedListKey, T>(
  salesforceAccountId: string,
  list: K,
  toRow: (row: AccountRelatedRow<K>) => T,
) {
  const idQuery = useGetContractingAccountId(salesforceAccountId);
  const summaryQuery = useGetAccountRelated(idQuery.data);
  const preview = summaryQuery.data?.[list];
  const records: ReadonlyArray<AccountRelatedRow<K>> | undefined = preview?.records;
  const rows = useMemo(() => (records ?? []).slice(0, RELATED_PREVIEW_LIMIT).map(toRow), [records, toRow]);
  const hasMore = !!preview?.has_more || (records?.length ?? 0) > RELATED_PREVIEW_LIMIT;

  return {
    // Past the preview limit, so the card reads "6+" however many rows the summary carries.
    count: hasMore ? RELATED_PREVIEW_LIMIT + 1 : rows.length,
    rows,
    ...toLoadState(idQuery, summaryQuery),
  };
}

export function useAccountRelatedFullList<K extends AccountRelatedListKey, T>(
  salesforceAccountId: string,
  list: K,
  toRow: (row: AccountRelatedRow<K>) => T,
) {
  const idQuery = useGetContractingAccountId(salesforceAccountId);
  const listQuery = useGetAccountRelatedList(idQuery.data, list);
  const { data } = listQuery;
  const rows = useMemo(() => (data?.records ?? []).map(toRow), [data, toRow]);

  return {
    count: data ? rows.length : undefined,
    isTruncated: data?.truncated ?? false,
    rows,
    ...toLoadState(idQuery, listQuery),
  };
}
